import React, { Fragment, useCallback, useMemo } from 'react';

import { useDrawer } from '~/hooks/useDrawer';
import { LOAN_TYPE_NAMES } from '~/typings/enums/LoanType';
import { Button, Flex, RenderRichText, Shimmer, Tag, Typography } from '~/ui/components';
import { useAwaitControl } from 'react-redux-await-control';
import { getTicketStatus } from '~/store/tickets/actions';
import { Accompaniment } from '~/typings/entities/Accompaniment';
import { getTicketStatusColor } from '~/common/tickets';
import { findLastNameById } from '~/common/selectValues';
import moment from 'moment';
import Conversation from '~/ui/components/Conversation/Conversation';
import CommentTypeBadge from '~/components/CommentTypeBadge/CommentTypeBadge';
import { CommentTypeName } from '~/typings/entities/Ticket';
import {
  Wrapper,
  TimeSection,
  Circle,
  Divisor,
  Line,
  LineContent,
  Timeline,
  Content,
  InfoContent,
  Action,
  CommentsWrapper,
} from './History.styles';

interface HistoryProps {
  id: string;
  accompaniments: Accompaniment[];
  isLoading?: boolean;
}

function History({ id, accompaniments, isLoading }: HistoryProps) {
  const { openDrawer } = useDrawer();

  const getTicketStatusControl = useAwaitControl(getTicketStatus);

  const ticketStatus = getTicketStatusControl.result();

  const handleConversation = useCallback(
    (conversationId) => {
      if (conversationId) {
        openDrawer(
          'ticket-conversation',
          <CommentsWrapper>
            <Conversation conversationId={conversationId} />
          </CommentsWrapper>,
          {
            title: 'Histórico de conversas',
            closable: true,
            width: 410,
            sideBySide: true,
          },
        );
      }
    },
    [id],
  );

  const renderValue = (detail) => {
    const statusName = findLastNameById(ticketStatus, Number(detail?.value)) || detail?.value;

    if (detail?.type === 'Status' && detail?.value?.length) {
      return <Tag {...getTicketStatusColor(ticketStatus, Number(detail?.value))}>{statusName}</Tag>;
    }

    if (detail?.type === 'Product') {
      return <Typography type="caption">{LOAN_TYPE_NAMES?.[detail?.value] || detail?.value}</Typography>;
    }

    if (detail?.type === 'Commentary') {
      return <RenderRichText html={detail.value} />;
    }

    return <Typography type="caption"> {detail?.value}</Typography>;
  };

  const getDirection = (detail) => {
    if (detail?.type === 'Commentary') {
      return 'column';
    }

    return 'row';
  };

  const orderedAccompaniments = useMemo(
    () =>
      [...(accompaniments || [])].sort((a, b) => {
        const dateA = moment(a?.date).valueOf() || 0;
        const dateB = moment(b?.date).valueOf() || 0;
        return dateB - dateA;
      }),
    [accompaniments],
  );

  if (isLoading) {
    return (
      <Wrapper>
        <Timeline>
          <TimeSection>
            <Shimmer width={136} height={20} mb={16} />
            <Shimmer width={136} height={20} mb={16} />
          </TimeSection>
          <Divisor>
            <Circle />
            <LineContent>
              <Line position="right" />
              <Line position="left" />
            </LineContent>
          </Divisor>
          <Content gap={8} mb={20}>
            <Shimmer width={250} height={20} mb={16} />
            <Content gap={4}>
              <Shimmer width={136} height={20} mb={16} />
              <Shimmer width={136} height={20} mb={16} />
              <Shimmer width={136} height={20} mb={16} />
            </Content>
          </Content>
        </Timeline>
      </Wrapper>
    );
  }

  if (!orderedAccompaniments.length) {
    return (
      <Wrapper>
        <Typography type="bodySmall" weight={600}>
          Histórico não encontrado
        </Typography>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {orderedAccompaniments.map((accompaniment, index) => (
        <Timeline key={index}>
          <TimeSection>
            <Typography type="footnote">{moment(accompaniment?.date).format('DD MMM YYYY')}</Typography>
            <Typography type="footnote">{moment(accompaniment?.date).format('HH[h]mm')}</Typography>
          </TimeSection>
          <Divisor>
            <Circle active={index === 0} />
            <LineContent>
              <Line position="right" active={index === 0} />
              <Line position="left" active={index === 0} />
            </LineContent>
          </Divisor>
          <Content gap={8} mb={20}>
            <Typography type="paragraphSmall" weight={700}>
              {accompaniment?.title}
            </Typography>
            <Content gap={4}>
              {(() => {
                const commentTypeValue = accompaniment?.details?.find((d) => d?.type === 'CommentType')?.value;

                return accompaniment?.details?.map((detail, i) => {
                  // The comment type is shown inline next to the "Comentário:" label, so the
                  // standalone "Tipo do comentário" row is not rendered on its own.
                  if (detail?.type === 'CommentType') {
                    return null;
                  }

                  const isTypeConversation = detail?.type === 'conversationId' && detail?.value?.length;
                  const isCommentary = detail?.type === 'Commentary';
                  const label = detail?.label?.length ? `${detail?.label}:` : '';

                  return (
                    <Fragment key={i}>
                      {isTypeConversation ? (
                        <Action>
                          <Button
                            variant="text"
                            icon="Message"
                            size="sm"
                            onClick={() => handleConversation(detail?.value)}
                          >
                            Acessar conversa
                          </Button>
                        </Action>
                      ) : (
                        <InfoContent direction={getDirection(detail)}>
                          {isCommentary ? (
                            <Flex align="center" gap={8}>
                              <Typography type="caption" weight={700}>
                                {label}
                              </Typography>
                              {commentTypeValue?.length && (
                                <CommentTypeBadge type={commentTypeValue.toUpperCase() as CommentTypeName} />
                              )}
                            </Flex>
                          ) : (
                            <Typography type="caption" weight={700}>
                              {label}
                            </Typography>
                          )}

                          {renderValue(detail)}
                        </InfoContent>
                      )}
                    </Fragment>
                  );
                });
              })()}
            </Content>
          </Content>
        </Timeline>
      ))}
    </Wrapper>
  );
}

export default History;
